// ─── CHAD V3 Data Service ────────────────────────────────────────────────────
// Fetches live project data from Google Sheets via /api/sheets.
// No sample data fallback — if Sheets is unreachable, we show an error.
// ─────────────────────────────────────────────────────────────────────────────

const cache = {};

export async function fetchProjectData(projectNumber, mode = "pm") {
  const cacheKey = `${projectNumber}_${mode}`;

  // Return cached data if fresh (5 min TTL)
  if (cache[cacheKey] && Date.now() - cache[cacheKey].fetchedAt < 300000) {
    return { ok: true, datasets: cache[cacheKey].datasets, fromCache: true };
  }

  try {
    const res = await fetch("/api/sheets", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ projectNumber, mode }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      return { ok: false, error: err.error || `Sheets API returned ${res.status}`, datasets: null };
    }

    const data = await res.json();

    if (!data.ok) {
      return { ok: false, error: data.error || "Unknown error from Sheets API", datasets: null };
    }

    if (!data.datasets || Object.keys(data.datasets).length === 0) {
      return { ok: false, error: "No datasets found for this project. Check that the Google Sheets are shared with the service account.", datasets: null };
    }

    // Cache the result
    cache[cacheKey] = { datasets: data.datasets, fetchedAt: Date.now() };

    return {
      ok: true,
      datasets: data.datasets,
      datasetsLoaded: data.datasetsLoaded,
      summariesLoaded: data.summariesLoaded || 0,
      fromCache: false,
    };
  } catch (err) {
    return { ok: false, error: `Network error: ${err.message}`, datasets: null };
  }
}

// Fetch portfolio data for exec mode — all projects combined
export async function fetchPortfolioData(projects) {
  const results = await Promise.allSettled(
    projects.map(p => fetchProjectData(p.number, "exec"))
  );

  const merged = {
    rfis: { label: "RFI Log", rows: [] },
    submittals: { label: "Submittal Log", rows: [] },
    changeOrders: { label: "Change Order Log", rows: [] },
    schedule: { label: "Schedule", rows: [] },
    quantities: { label: "BIM Quantities", rows: [] },
    contracts: { label: "Contracts", rows: [] },
  };

  const errors = [];

  results.forEach((result, i) => {
    const proj = projects[i];
    if (result.status === "fulfilled" && result.value.ok) {
      const ds = result.value.datasets;
      Object.keys(merged).forEach(key => {
        if (ds[key]?.rows?.length) {
          // Tag rows with project info for exec context
          const tagged = ds[key].rows.map(r => ({
            ...r,
            _project: proj.name,
            _number: proj.number,
          }));
          merged[key].rows.push(...tagged);
        }
      });
    } else {
      errors.push(`${proj.name}: ${result.value?.error || "Failed to load"}`);
    }
  });

  // Remove empty datasets
  const datasets = Object.fromEntries(
    Object.entries(merged).filter(([, v]) => v.rows.length > 0)
  );

  return {
    ok: Object.keys(datasets).length > 0,
    datasets,
    errors: errors.length > 0 ? errors : null,
  };
}

export function invalidateCache(projectNumber) {
  if (projectNumber) {
    Object.keys(cache).forEach(key => {
      if (key.startsWith(projectNumber)) delete cache[key];
    });
  } else {
    Object.keys(cache).forEach(key => delete cache[key]);
  }
}

export function clearAllCache() {
  Object.keys(cache).forEach(key => delete cache[key]);
}
